import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";

import DocumentEditor from "./document-editor/DocumentEditor";
import { IMessageMetadata } from "../../types/chat";
import type { AppealLetterFormat } from "../../services/apiService";

interface MessageEditorProps {
  initialContent: string;
  position: "left" | "right";
  onClose: () => void;
  onBack?: () => void;
  canGoBack?: boolean;
  onTogglePosition: () => void;
  onSave?: (content: string) => void | Promise<void>;
  onChange?: (content: string) => void;
  onDownload?: (format: AppealLetterFormat) => Promise<void>;
  metadata?: IMessageMetadata | null;
  disableEditMode?: boolean;
  claimId?: string;
}

export interface MessageEditorRef {
  getCurrentContent: () => string;
  triggerSave: () => Promise<void>;
}

const MessageEditor = forwardRef<MessageEditorRef, MessageEditorProps>(
  (
    {
      initialContent,
      onSave,
      onChange,
      onDownload,
      onTogglePosition,
      onClose,
      onBack,
      canGoBack,
      metadata,
      disableEditMode,
      claimId,
    },
    ref,
  ) => {
    const latestContentRef = useRef(initialContent);

    useEffect(() => {
      latestContentRef.current = initialContent;
    }, [initialContent]);

    const handleChange = (newContent: string) => {
      latestContentRef.current = newContent;
      onChange?.(newContent);
    };

    const handleSave = async (newContent: string) => {
      latestContentRef.current = newContent;
      if (onSave) {
        await onSave(newContent);
      }
    };

    useImperativeHandle(
      ref,
      () => ({
        getCurrentContent: () => latestContentRef.current,
        triggerSave: async () => {
          const currentContent = latestContentRef.current;
          if (onSave) {
            await onSave(currentContent);
          }
        },
      }),
      [onSave],
    );

    return (
      <div className="h-full flex flex-col bg-white">
        <div className="flex-1 overflow-hidden">
          <DocumentEditor
            content={initialContent}
            onChange={handleChange}
            onSave={handleSave}
            onDownload={onDownload}
            onClose={onClose}
            onBack={onBack}
            canGoBack={canGoBack}
            onTogglePosition={onTogglePosition}
            metadata={metadata}
            disableEditMode={disableEditMode}
            claimId={claimId}
          />
        </div>
      </div>
    );
  },
);

export default MessageEditor;
